import { ClockCircleOutlined, DeleteOutlined, PlusOutlined, SendOutlined, QuestionCircleOutlined, UserOutlined, StopOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, message, Modal, Select, Space, Table, Tag, Tour, ConfigProvider, Spin, Radio } from 'antd';
import type { TourProps } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { broadcastService } from '../services/broadcastService';
import { messageService } from '../services/messageService';
import { userService } from '../services/userService';
import { Message } from '../types/message';
import { configService } from '../services/configService';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';
import type { User } from '../types/users';

const socket = io('https://api-coffee.8am.vn'); // Update with your backend URL

const { Search } = Input;
const { Option } = Select;
const ZALO_CONSULTING_ENDPOINT = 'https://openapi.zalo.me/v3.0/oa/message/cs';
const ZALO_BROADCAST_BANNER_MAX_SIZE = '1MB';

interface Broadcast {
  id: string;
  messageId: string;
  message?: Message;
  userTags: string[];
  recipients?: string[];
  status: 'pending' | 'sending' | 'completed' | 'failed' | 'scheduled';
  totalRecipients: number;
  sentCount: number;
  createdAt: any;
  updatedAt: any;
  scheduledTime?: any;
}

const toTimestamp = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const assertZaloSuccess = (response: any) => {
  const data = response?.data;
  if (data && typeof data.error !== 'undefined' && data.error !== 0) {
    throw new Error(data.message || data.error_message || `Zalo từ chối gửi tin (error ${data.error})`);
  }
};

const validateBroadcastBanner = (selectedMessage: Message) => {
  const banner = selectedMessage.template_data?.banner;
  if (banner?.image_url && banner.max_size && banner.max_size !== ZALO_BROADCAST_BANNER_MAX_SIZE) {
    throw new Error(
      `Banner của tin "${selectedMessage.template_data?.header?.content || 'không có tiêu đề'}" đang vượt giới hạn ${ZALO_BROADCAST_BANNER_MAX_SIZE} của Zalo. ` +
      'Vui lòng vào Quản lý tin truyền thông, sửa tin và tải lại ảnh; hệ thống sẽ tự nén ảnh trước khi lưu.'
    );
  }
};

const normalizeToPlainText = (htmlContent?: string) => {
  if (!htmlContent) return '';
  return htmlContent
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const sanitizeBroadcastButtons = (buttons?: Message['template_data'] extends infer T
  ? T extends { buttons?: infer B }
    ? B
    : never
  : never) => {
  if (!Array.isArray(buttons)) return [];

  return buttons
    .filter((button): button is NonNullable<typeof button> => Boolean(button?.title && button?.type))
    .map((button) => ({
      title: button.title,
      type: button.type as string,
      payload: button.payload
    }));
};

const BroadcastManagement: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string>('');
  const [scheduledDateTime, setScheduledDateTime] = useState<moment.Moment | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [sendingBroadcasts, setSendingBroadcasts] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [userTagFilter, setUserTagFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 400;
  const [users, setUsers] = useState<any[]>([]);
  const [sendSingleUserModalVisible, setSendSingleUserModalVisible] = useState(false);
  const [sendSingleUserForm] = Form.useForm();
  const [sendSingleUserLoading, setSendSingleUserLoading] = useState(false);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [cancelingBroadcasts, setCancelingBroadcasts] = useState<Set<string>>(new Set());
  const cancelRequestedBroadcastsRef = useRef<Set<string>>(new Set());
  const scheduledRunningBroadcastsRef = useRef<Set<string>>(new Set());
  const [sendBroadcastModalVisible, setSendBroadcastModalVisible] = useState(false);
  const [sendingBroadcastRecord, setSendingBroadcastRecord] = useState<Broadcast | null>(null);
  const [sendBroadcastForm] = Form.useForm();
  const isMountedRef = useRef(true);
  const messagesCacheRef = useRef<Map<string, Message>>(new Map());
  const sendingBroadcastsRef = useRef<Set<string>>(new Set());
  const broadcastsRef = useRef<Broadcast[]>([]);
  const broadcastPollInFlightRef = useRef(false);
  const hasLoadedSingleSendUsersRef = useRef(false);
  const hasInitializedDataRef = useRef(false);

  // Tour state and refs
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isModalTourOpen, setIsModalTourOpen] = useState(false);
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const filtersRef = useRef(null);
  const addButtonRef = useRef(null);
  const tableContainerRef = useRef(null);

  // References for modal tour targets
  const modalTitleRef = useRef(null);
  const messageFieldRef = useRef(null);
  const userTagsFieldRef = useRef(null);
  const submitButtonRef = useRef(null);

  const availableUsersWithZalo = useMemo(() => users.filter((u) => u?.id && u?.zaloUserId), [users]);
  const messageLookup = useMemo(() => {
    return messages.reduce<Record<string, Message>>((accumulator, item) => {
      if (item?.id) {
        accumulator[item.id] = item;
      }
      return accumulator;
    }, {});
  }, [messages]);

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý broadcast',
      description: 'Chào mừng bạn đến với trang Quản lý broadcast! Đây là nơi bạn có thể tạo và quản lý các chiến dịch gửi tin nhắn hàng loạt đến khách hàng theo các nhóm mục tiêu cụ thể.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm broadcast',
      description: 'Tìm kiếm nhanh các broadcast theo tiêu đề hoặc nội dung. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
      target: () => searchRef.current,
    },
    {
      title: 'Bộ lọc',
      description: 'Lọc danh sách broadcast theo nhãn người dùng và trạng thái. Bạn có thể kết hợp các bộ lọc để tìm chính xác broadcast cần quản lý.',
      target: () => filtersRef.current,
    },
    {
      title: 'Tạo broadcast mới',
      description: 'Nhấn vào đây để tạo một chiến dịch broadcast mới. Bạn sẽ chọn tin nhắn template và nhóm người dùng để gửi đến.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Bảng dữ liệu broadcast',
      description: 'Bảng hiển thị tất cả các broadcast đã tạo với thông tin về trạng thái, số người nhận, tiến độ gửi và các thao tác như gửi ngay, hẹn giờ hoặc xóa.',
      target: () => tableContainerRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể tạo và quản lý các chiến dịch broadcast hiệu quả để tiếp cận khách hàng.',
      target: () => titleRef.current,
    },
  ];

  const modalTourSteps: TourProps['steps'] = [
    {
      title: 'Form tạo broadcast',
      description: 'Đây là form để tạo chiến dịch broadcast mới. Broadcast cho phép gửi tin nhắn hàng loạt đến nhiều khách hàng cùng một lúc theo nhóm mục tiêu cụ thể.',
      target: () => modalTitleRef.current,
    },
    {
      title: 'Chọn tin nhắn template',
      description: 'Chọn tin nhắn template đã tạo trước đó để sử dụng trong broadcast này. Tin nhắn template chứa banner, tiêu đề, nội dung và nút thao tác. Đây là thông tin bắt buộc.',
      target: () => messageFieldRef.current,
    },
    {
      title: 'Chọn nhóm người dùng',
      description: 'Chọn nhãn (tag) của nhóm người dùng muốn gửi broadcast. Có thể chọn nhiều nhãn hoặc không chọn nhãn nào để gửi cho tất cả người dùng. Điều này giúp target đúng đối tượng khách hàng.',
      target: () => userTagsFieldRef.current,
    },
    {
      title: 'Hoàn thành',
      description: 'Nhấn "Tạo Broadcast" để tạo chiến dịch broadcast. Sau khi tạo, bạn có thể chọn gửi ngay hoặc hẹn giờ gửi cho chiến dịch này từ danh sách broadcast.',
      target: () => submitButtonRef.current,
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    messages.forEach((item) => {
      if (item?.id) {
        messagesCacheRef.current.set(item.id, item);
      }
    });
  }, [messages]);

  useEffect(() => {
    sendingBroadcastsRef.current = sendingBroadcasts;
  }, [sendingBroadcasts]);

  useEffect(() => {
    broadcastsRef.current = broadcasts;
  }, [broadcasts]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      if (isMountedRef.current) {
        setDebouncedSearchText(searchText);
      }
    }, 250);

    return () => window.clearTimeout(debounceTimer);
  }, [searchText]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const fetchUsers = useCallback(async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
      if (!hasLoadedSingleSendUsersRef.current) {
        setUsersList(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Không thể tải danh sách người dùng');
    }
  }, []);

  const attachMessagesToBroadcasts = useCallback(async (rawBroadcasts: any[], sourceMessages?: Message[]) => {
    const messageMap = new Map<string, Message>(messagesCacheRef.current);
    const baseMessages = sourceMessages ?? messages;

    baseMessages.forEach((item) => {
      if (item?.id) {
        messageMap.set(item.id, item);
      }
    });

    const missingMessageIds = Array.from(
      new Set(
        rawBroadcasts
          .map((broadcast) => broadcast?.messageId)
          .filter((id): id is string => !!id && !messageMap.has(id))
      )
    );

    if (missingMessageIds.length > 0) {
      const missingMessages = await Promise.all(
        missingMessageIds.map(async (messageId) => {
          try {
            return await messageService.getMessage(messageId);
          } catch (error) {
            console.error(`Error fetching message ${messageId}:`, error);
            return null;
          }
        })
      );

      missingMessages.forEach((item) => {
        if (item?.id) {
          messageMap.set(item.id, item);
        }
      });

      messagesCacheRef.current = messageMap;
    }

    return rawBroadcasts.map((broadcast: any) => ({
      ...broadcast,
      message: broadcast?.messageId ? messageMap.get(broadcast.messageId) : undefined
    }));
  }, [messages]);

  const fetchBroadcasts = useCallback(async (sourceMessages?: Message[]) => {
    setLoading(true);
    try {
      const data = await broadcastService.getAllBroadcasts();
      const broadcastsWithMessages = await attachMessagesToBroadcasts(data, sourceMessages);
      setBroadcasts(broadcastsWithMessages);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
      message.error('Không thể tải danh sách broadcast');
    } finally {
      setLoading(false);
    }
  }, [attachMessagesToBroadcasts]);

  const fetchMessages = useCallback(async (): Promise<Message[]> => {
    try {
      const data = await messageService.getAllMessages();
      setMessages(data);
      data.forEach((item) => {
        if (item?.id) {
          messagesCacheRef.current.set(item.id, item);
        }
      });
      return data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      message.error('Không thể tải danh sách tin nhắn');
      return [];
    }
  }, []);

  const fetchUserTags = useCallback(async () => {
    try {
      const data = await userService.getUserTags();
      setUserTags(data);
    } catch (error) {
      console.error('Error fetching user tags:', error);
      message.error('Không thể tải danh sách nhãn người dùng');
    }
  }, []);

  useEffect(() => {
    if (hasInitializedDataRef.current) {
      return;
    }

    hasInitializedDataRef.current = true;

    const initializePageData = async () => {
      const [loadedMessages] = await Promise.all([
        fetchMessages(),
        fetchUserTags(),
        fetchUsers()
      ]);
      await fetchBroadcasts(loadedMessages);
    };

    initializePageData();
  }, [fetchBroadcasts, fetchMessages, fetchUserTags, fetchUsers]);

  useEffect(() => {
    if (sendingBroadcasts.size === 0) return;

    const interval = setInterval(async () => {
      if (broadcastPollInFlightRef.current) {
        return;
      }

      if (sendingBroadcastsRef.current.size === 0) {
        return;
      }

      try {
        broadcastPollInFlightRef.current = true;
        const data = await broadcastService.getAllBroadcasts();
        const broadcastsWithMessages = await attachMessagesToBroadcasts(data);

        setBroadcasts(broadcastsWithMessages);

        const updatedSendingBroadcasts = new Set(sendingBroadcastsRef.current);
        broadcastsWithMessages.forEach(broadcast => {
          if (sendingBroadcastsRef.current.has(broadcast.id) &&
            (broadcast.status === 'completed' || broadcast.status === 'failed')) {
            updatedSendingBroadcasts.delete(broadcast.id);
            message.info(`Broadcast ${broadcast.status === 'completed' ? 'đã gửi thành công' : 'gửi thất bại'}`);
          }
        });

        setSendingBroadcasts(updatedSendingBroadcasts);

        if (updatedSendingBroadcasts.size === 0) {
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Error checking broadcast status:', error);
      } finally {
        broadcastPollInFlightRef.current = false;
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [sendingBroadcasts, attachMessagesToBroadcasts]);

  const loadUsersForSingleSend = async () => {
    if (usersList.length > 0) return;

    if (users.length > 0) {
      setUsersList(users);
      hasLoadedSingleSendUsersRef.current = true;
      return;
    }

    if (hasLoadedSingleSendUsersRef.current) {
      return;
    }

    setLoadingUsers(true);
    try {
      const data = await userService.getAllUsers();
      setUsersList(data);
      hasLoadedSingleSendUsersRef.current = true;
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Không thể tải danh sách người dùng');
    } finally {
      setLoadingUsers(false);
    }
  };

  const sendConsultingMessageToUser = useCallback(async (zaloUserId: string, selectedMessage: Message, accessToken: string) => {
    validateBroadcastBanner(selectedMessage);

    const templateData = selectedMessage.template_data;
    const bannerImageUrl = templateData?.banner?.image_url?.trim();
    const messageButtons = sanitizeBroadcastButtons(selectedMessage.template_data?.buttons);
    const textSections = [
      templateData?.header?.content,
      templateData?.table?.rows?.map((row) => `${row.key}: ${row.value}`).join('\n'),
      templateData?.text?.content,
      selectedMessage.content
    ]
      .map((section) => normalizeToPlainText(section))
      .filter(Boolean);
    const combinedText = textSections.join('\n\n').trim();

    if (!combinedText && !bannerImageUrl) {
      throw new Error('Tin nhắn đã chọn không có nội dung để gửi');
    }

    if (bannerImageUrl) {
      const imageMessageData = {
        recipient: {
          user_id: zaloUserId
        },
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'media',
              elements: [
                {
                  media_type: 'image',
                  url: bannerImageUrl
                }
              ]
            }
          }
        }
      };

      const imageResponse = await axios.post(ZALO_CONSULTING_ENDPOINT, imageMessageData, {
        headers: {
          'access_token': accessToken,
          'Content-Type': 'application/json'
        }
      });

      assertZaloSuccess(imageResponse);
    }

    if (combinedText) {
      const textMessageData: any = {
        recipient: {
          user_id: zaloUserId
        },
        message: {
          text: combinedText
        }
      };

      if (messageButtons.length > 0) {
        textMessageData.message.attachment = {
          type: 'template',
          payload: {
            buttons: messageButtons
          }
        };
      }

      const textResponse = await axios.post(ZALO_CONSULTING_ENDPOINT, textMessageData, {
        headers: {
          'access_token': accessToken,
          'Content-Type': 'application/json'
        }
      });

      assertZaloSuccess(textResponse);
    }
  }, []);

  const resolveBroadcastRecipients = useCallback((record: Broadcast) => {
    if (record.recipients && record.recipients.length > 0) {
      const selectedUserIdSet = new Set(record.recipients);
      return availableUsersWithZalo.filter((u) => selectedUserIdSet.has(u.id));
    }

    if (record.userTags && record.userTags.length > 0) {
      const selectedTagSet = new Set(record.userTags);
      return availableUsersWithZalo.filter((u) => selectedTagSet.has(u.tagNames));
    }

    return availableUsersWithZalo;
  }, [availableUsersWithZalo]);

  const handleSendToSingleUser = async (values: any) => {
    try {
      setSendSingleUserLoading(true);
      const selectedMessage = messages.find(m => m.id === values.messageId);
      if (!selectedMessage) {
        message.error('Vui lòng chọn tin nhắn');
        return;
      }

      const config = await configService.getConfig();
      const selectedUser = usersList.find(u => u.id === values.userId);
      if (!selectedUser?.zaloUserId) {
        message.error('Người dùng này chưa có Zalo User ID');
        return;
      }

      if (!config?.access_token_zalo) {
        message.error('Thiếu access token Zalo trong cấu hình hệ thống');
        return;
      }

      await sendConsultingMessageToUser(selectedUser.zaloUserId, selectedMessage, config.access_token_zalo);

      message.success('Gửi tin nhắn thành công');
      setSendSingleUserModalVisible(false);
      sendSingleUserForm.resetFields();
    } catch (error: any) {
      console.error('Error sending message to single user:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Có lỗi xảy ra khi gửi tin nhắn';
      message.error(errorMessage);
    } finally {
      setSendSingleUserLoading(false);
    }
  };

  const openSendSingleUserModal = () => {
    setSendSingleUserModalVisible(true);
    loadUsersForSingleSend();
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);
      const recipientMode = values.recipientMode || 'tags';
      const selectedUserIds: string[] = recipientMode === 'users' ? (values.selectedUserIds || []) : [];
      const selectedTags: string[] = recipientMode === 'tags' ? (values.userTags || []) : [];

      const estimatedRecipients = (() => {
        if (recipientMode === 'all') return availableUsersWithZalo.length;
        if (recipientMode === 'users') return selectedUserIds.length;
        if (selectedTags.length === 0) return availableUsersWithZalo.length;
        return availableUsersWithZalo.filter((u) => selectedTags.includes(u.tagNames)).length;
      })();

      const broadcastData = {
        messageId: values.messageId,
        userTags: selectedTags,
        status: 'pending' as const,
        title: '',
        content: '',
        recipients: selectedUserIds,
        totalRecipients: estimatedRecipients,
        sentCount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await broadcastService.addBroadcast(broadcastData);
      message.success('Tạo broadcast thành công');
      setModalVisible(false);
      form.resetFields();
      fetchBroadcasts(messages);
    } catch (error) {
      console.error('Error creating broadcast:', error);
      message.error('Có lỗi xảy ra khi tạo broadcast');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa broadcast này không?',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await broadcastService.deleteBroadcast(id);
          message.success('Xóa broadcast thành công');
          fetchBroadcasts(messages);
        } catch (error) {
          console.error('Error deleting broadcast:', error);
          message.error('Không thể xóa broadcast');
        }
      }
    });
  };

  const executeBroadcastSend = useCallback(async (record: Broadcast, targetUsers: any[]) => {
    const totalTargets = targetUsers.length;
    if (!record.id) {
      message.error('Không xác định được broadcast');
      return;
    }

    try {
      cancelRequestedBroadcastsRef.current.delete(record.id);
      setCancelingBroadcasts(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
      setSendingBroadcasts(prev => new Set(prev).add(record.id));
      const selectedMessage = messages.find((m) => m.id === record.messageId);
      if (!selectedMessage) {
        throw new Error('Không tìm thấy nội dung tin nhắn của broadcast');
      }
      validateBroadcastBanner(selectedMessage);

      const config = await configService.getConfig();
      if (!config?.access_token_zalo) {
        throw new Error('Thiếu access token Zalo trong cấu hình hệ thống');
      }

      await broadcastService.updateBroadcast(record.id, {
        recipients: record.recipients || [],
        userTags: record.userTags || [],
        status: 'sending',
        totalRecipients: totalTargets,
        sentCount: 0,
        updatedAt: new Date()
      });

      setBroadcasts((prev) => prev.map((item) => (
        item.id === record.id
          ? { ...item, recipients: record.recipients || [], userTags: record.userTags || [], status: 'sending', totalRecipients: totalTargets, sentCount: 0 }
          : item
      )));

      let sentCount = 0;
      let isCancelled = false;

      for (const targetUser of targetUsers) {
        if (cancelRequestedBroadcastsRef.current.has(record.id)) {
          isCancelled = true;
          break;
        }

        if (!targetUser?.zaloUserId) continue;
        try {
          await sendConsultingMessageToUser(targetUser.zaloUserId, selectedMessage, config.access_token_zalo);
          const nextSentCount = sentCount + 1;
          sentCount = nextSentCount;
          setBroadcasts((prev) => prev.map((item) => (
            item.id === record.id
              ? { ...item, sentCount: nextSentCount, totalRecipients: totalTargets }
              : item
          )));
        } catch (sendError) {
          console.error('Error sending broadcast message to user:', targetUser?.id, sendError);
        }
      }

      const finalStatus = isCancelled ? 'failed' : (sentCount === totalTargets ? 'completed' : 'failed');
      await broadcastService.updateBroadcast(record.id, {
        status: finalStatus,
        totalRecipients: totalTargets,
        sentCount,
        updatedAt: new Date()
      });

      setBroadcasts((prev) => prev.map((item) => (
        item.id === record.id
          ? { ...item, status: finalStatus, totalRecipients: totalTargets, sentCount }
          : item
      )));

      if (isCancelled) {
        message.warning(`Đã hủy gửi broadcast giữa chừng (${sentCount}/${totalTargets})`);
      } else if (sentCount === totalTargets) {
        message.success(`Gửi broadcast thành công (${sentCount}/${totalTargets})`);
      } else {
        message.warning(`Broadcast gửi một phần (${sentCount}/${totalTargets}). Vui lòng kiểm tra lại người nhận.`);
      }
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      await broadcastService.updateBroadcast(record.id, {
        status: 'failed',
        updatedAt: new Date()
      });
      setBroadcasts((prev) => prev.map((item) => (
        item.id === record.id ? { ...item, status: 'failed' } : item
      )));
      message.error(error?.message || 'Không thể gửi broadcast');
    } finally {
      cancelRequestedBroadcastsRef.current.delete(record.id);
      setCancelingBroadcasts(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
      setSendingBroadcasts(prev => {
        const newSet = new Set(prev);
        newSet.delete(record.id);
        return newSet;
      });
    }
  }, [messages, sendConsultingMessageToUser]);

  const handleSendBroadcast = (record: Broadcast) => {
    setSendingBroadcastRecord(record);
    const mode = record.recipients && record.recipients.length > 0
      ? 'users'
      : (record.userTags && record.userTags.length > 0 ? 'tags' : 'all');
    sendBroadcastForm.setFieldsValue({
      recipientMode: mode,
      selectedUserIds: record.recipients || [],
      userTags: record.userTags || []
    });
    setSendBroadcastModalVisible(true);
  };

  const handleConfirmSendBroadcast = async (values: any) => {
    if (!sendingBroadcastRecord) return;

    const recipientMode = values.recipientMode || 'all';
    const selectedUserIds: string[] = recipientMode === 'users' ? (values.selectedUserIds || []) : [];
    const selectedTags: string[] = recipientMode === 'tags' ? (values.userTags || []) : [];

    const nextRecord: Broadcast = {
      ...sendingBroadcastRecord,
      recipients: recipientMode === 'users' ? selectedUserIds : [],
      userTags: recipientMode === 'tags' ? selectedTags : []
    };

    const targetUsers = resolveBroadcastRecipients(nextRecord);
    const totalTargets = targetUsers.length;
    const previewNames = targetUsers
      .slice(0, 5)
      .map((user) => user.name || user.phoneNumber || 'Khách hàng')
      .join(', ');

    setSendBroadcastModalVisible(false);
    setSendingBroadcastRecord(null);

    Modal.confirm({
      title: 'Xác nhận gửi broadcast',
      content: totalTargets > 0 ? (
        <div>
          <p>Bạn sắp gửi broadcast cho <strong>{totalTargets}</strong> người dùng.</p>
          <p>Danh sách mẫu: {previewNames}{totalTargets > 5 ? ', ...' : ''}</p>
          <p style={{ marginBottom: 0 }}>Bấm <strong>Gửi ngay</strong> để xác nhận, hoặc <strong>Hủy</strong> để quay lại.</p>
        </div>
      ) : 'Không có người dùng hợp lệ (đã theo OA + có Zalo User ID) để gửi.',
      okText: 'Gửi ngay',
      cancelText: 'Đóng',
      okButtonProps: { disabled: totalTargets === 0 },
      onOk: async () => {
        await executeBroadcastSend(nextRecord, targetUsers);
      }
    });
  };

  const handleCancelSendingBroadcast = (record: Broadcast) => {
    if (!record.id) return;
    Modal.confirm({
      title: 'Xác nhận hủy gửi broadcast',
      content: 'Broadcast đang gửi sẽ dừng ở các người nhận chưa gửi. Bạn có muốn hủy ngay không?',
      okText: 'Hủy gửi ngay',
      okType: 'danger',
      cancelText: 'Đóng',
      onOk: () => {
        cancelRequestedBroadcastsRef.current.add(record.id);
        setCancelingBroadcasts(prev => new Set(prev).add(record.id));
        message.info('Đã ghi nhận yêu cầu hủy. Hệ thống sẽ dừng sau lượt gửi hiện tại.');
      }
    });
  };

  const handleScheduleBroadcast = async (record: Broadcast) => {
    setSelectedBroadcastId(record.id);
    setScheduleModalVisible(true);
  };

  const handleScheduleSubmit = async () => {
    if (!scheduledDateTime) {
      message.error('Vui lòng chọn thời gian gửi');
      return;
    }

    try {
      setScheduleLoading(true);
      await broadcastService.updateBroadcast(selectedBroadcastId, {
        status: 'scheduled',
        scheduledTime: scheduledDateTime.toDate(),
        updatedAt: new Date()
      });
      setBroadcasts(prev => prev.map(b =>
        b.id === selectedBroadcastId
          ? { ...b, status: 'scheduled', scheduledTime: scheduledDateTime.toDate() }
          : b
      ));
      message.success('Đã hẹn giờ gửi broadcast thành công');
      setScheduleModalVisible(false);
      setScheduledDateTime(null);
    } catch (error) {
      console.error('Error scheduling broadcast:', error);
      message.error('Không thể lên lịch gửi broadcast');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleCancelSchedule = async (record: Broadcast) => {
    Modal.confirm({
      title: 'Xác nhận hủy lịch',
      content: 'Bạn có chắc chắn muốn hủy lịch gửi broadcast này không?',
      okText: 'Hủy lịch',
      cancelText: 'Đóng',
      onOk: async () => {
        try {
          await broadcastService.updateBroadcast(record.id, {
            status: 'pending',
            scheduledTime: null,
            updatedAt: new Date()
          });
          setBroadcasts(prev => prev.map(b =>
            b.id === record.id
              ? { ...b, status: 'pending', scheduledTime: null }
              : b
          ));
          message.success('Đã hủy lịch gửi broadcast');
        } catch (error) {
          console.error('Error canceling scheduled broadcast:', error);
          message.error('Không thể hủy lịch gửi broadcast');
        }
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'gold';
      case 'sending':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Chờ gửi';
      case 'sending':
        return 'Đang gửi';
      case 'completed':
        return 'Hoàn thành';
      case 'failed':
        return 'Thất bại';
      case 'scheduled':
        return 'Đã lên lịch';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'Tin nhắn',
      dataIndex: 'messageId',
      key: 'message',
      render: (messageId: string) => {
        const message = messageLookup[messageId];
        return (
          <div>
            {message?.template_data?.banner && (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={message.template_data.banner.image_url}
                  alt="Banner"
                  style={{
                    width: 100,
                    height: 150,
                    objectFit: 'cover',
                    borderRadius: 4
                  }}
                />
              </div>
            )}
            <div>
              <strong>Tiêu đề:</strong> {message?.template_data?.header?.content}
            </div>
            <div>
              <strong>Nội dung:</strong>
              <div dangerouslySetInnerHTML={{ __html: message?.template_data?.text?.content || '' }} />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Nhãn người dùng',
      dataIndex: 'userTags',
      key: 'userTags',
      render: (tags: string[], record: Broadcast) => (
        <Space wrap>
          {record.recipients && record.recipients.length > 0 ? (
            <Tag color="blue">Danh sách người dùng ({record.recipients.length})</Tag>
          ) : tags && tags.length > 0 ? (
            tags.map(tag => (
              <Tag key={tag}>{tag}</Tag>
            ))
          ) : (
            <Tag>Tất cả người dùng</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Số người nhận',
      key: 'recipients',
      render: (record: Broadcast) => (
        <span>{record.sentCount}/{record.totalRecipients}</span>
      )
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (timestamp: any) => {
        if (!timestamp) return '';
        if (timestamp.seconds) {
          return moment(timestamp.seconds * 1000).format('DD/MM/YYYY HH:mm:ss');
        }
        return moment(timestamp).format('DD/MM/YYYY HH:mm:ss');
      }
    },
    {
      title: 'Thời gian hẹn',
      dataIndex: 'scheduledTime',
      key: 'scheduledTime',
      render: (scheduledTime: any) => {
        if (!scheduledTime) return '-';
        return moment(scheduledTime.seconds ? scheduledTime.seconds * 1000 : scheduledTime).format('DD/MM/YYYY HH:mm:ss');
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_: any, record: Broadcast) => (
        <Space>
          {record.status !== 'scheduled' && (
            <>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => handleSendBroadcast(record)}
                disabled={record.status === 'sending'}
              >
                Gửi
              </Button>
              <Button
                type="primary"
                icon={<ClockCircleOutlined />}
                onClick={() => handleScheduleBroadcast(record)}
                disabled={record.status === 'sending'}
              >
                Hẹn giờ
              </Button>
            </>
          )}
          {record.status === 'sending' && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancelSendingBroadcast(record)}
              loading={cancelingBroadcasts.has(record.id)}
            >
              Hủy gửi
            </Button>
          )}
          {record.status === 'scheduled' && (
            <Button
              type="primary"
              danger
              onClick={() => handleCancelSchedule(record)}
            >
              Hủy lịch
            </Button>
          )}
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            disabled={record.status === 'sending'}
          />
        </Space>
      ),
    },
  ];

  // Socket event handlers
  const handleBroadcastStatusUpdate = useCallback((data: {
    id: string;
    status: 'pending' | 'sending' | 'completed' | 'failed' | 'scheduled';
    totalRecipients?: number;
    sentCount?: number;
    error?: string;
  }) => {
    setBroadcasts(prev => prev.map(b =>
      b.id === data.id
        ? {
          ...b,
          status: data.status,
          ...(data.totalRecipients !== undefined && { totalRecipients: data.totalRecipients }),
          ...(data.sentCount !== undefined && { sentCount: data.sentCount })
        }
        : b
    ));

    if (data.status === 'completed') {
      message.success('Broadcast đã gửi thành công');
      setSendingBroadcasts(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.id);
        return newSet;
      });
    } else if (data.status === 'failed') {
      message.error(`Broadcast gửi thất bại${data.error ? ': ' + data.error : ''}`);
      setSendingBroadcasts(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.id);
        return newSet;
      });
    }
  }, []);

  const handleBroadcastProgress = useCallback((data: {
    id: string;
    sentCount: number;
    totalRecipients: number;
  }) => {
    setBroadcasts(prev => prev.map(b =>
      b.id === data.id
        ? { ...b, sentCount: data.sentCount, totalRecipients: data.totalRecipients }
        : b
    ));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const dueBroadcasts = broadcastsRef.current.filter((broadcast) => {
        if (broadcast.status !== 'scheduled' || !broadcast.scheduledTime || !broadcast.id) return false;
        if (scheduledRunningBroadcastsRef.current.has(broadcast.id)) return false;

        const scheduledTimeValue = toTimestamp(broadcast.scheduledTime);

        return !Number.isNaN(scheduledTimeValue) && scheduledTimeValue <= now;
      });

      dueBroadcasts.forEach(async (broadcast) => {
        if (!broadcast.id) return;
        scheduledRunningBroadcastsRef.current.add(broadcast.id);
        try {
          const targetUsers = resolveBroadcastRecipients(broadcast);
          await executeBroadcastSend(broadcast, targetUsers);
        } finally {
          scheduledRunningBroadcastsRef.current.delete(broadcast.id);
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [executeBroadcastSend, resolveBroadcastRecipients]);

  const handleBroadcastScheduled = useCallback((data: {
    id: string;
    scheduledTime: Date;
    status: 'scheduled';
  }) => {
    setBroadcasts(prev => prev.map(b =>
      b.id === data.id
        ? { ...b, scheduledTime: data.scheduledTime, status: data.status }
        : b
    ));
    message.success('Broadcast đã được lên lịch thành công');
  }, []);

  const handleBroadcastScheduleCancelled = useCallback((data: {
    id: string;
    status: 'pending';
  }) => {
    setBroadcasts(prev => prev.map(b =>
      b.id === data.id
        ? { ...b, scheduledTime: null, status: data.status }
        : b
    ));
    message.success('Đã hủy lịch gửi broadcast');
  }, []);

  // Socket connection setup
  useEffect(() => {
    // Socket event listeners
    socket.on('broadcastStatusUpdated', handleBroadcastStatusUpdate);
    socket.on('broadcastProgress', handleBroadcastProgress);
    socket.on('broadcastScheduled', handleBroadcastScheduled);
    socket.on('broadcastScheduleCancelled', handleBroadcastScheduleCancelled);

    // Cleanup on unmount
    return () => {
      socket.off('broadcastStatusUpdated', handleBroadcastStatusUpdate);
      socket.off('broadcastProgress', handleBroadcastProgress);
      socket.off('broadcastScheduled', handleBroadcastScheduled);
      socket.off('broadcastScheduleCancelled', handleBroadcastScheduleCancelled);
    };
  }, [handleBroadcastStatusUpdate, handleBroadcastProgress, handleBroadcastScheduled, handleBroadcastScheduleCancelled]);

  const filteredBroadcasts = useMemo(() => {
    const searchQuery = debouncedSearchText.toLowerCase().trim();

    return broadcasts.filter(broadcast => {
      const messageContent = broadcast.message?.template_data?.text?.content?.toLowerCase() || '';
      const messageHeader = broadcast.message?.template_data?.header?.content?.toLowerCase() || '';
      const matchesSearch = !searchQuery || messageContent.includes(searchQuery) || messageHeader.includes(searchQuery);
      const matchesUserTag = userTagFilter ? broadcast.userTags.includes(userTagFilter) : true;
      const matchesStatus = statusFilter ? broadcast.status === statusFilter : true;
      return matchesSearch && matchesUserTag && matchesStatus;
    });
  }, [broadcasts, debouncedSearchText, userTagFilter, statusFilter]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý broadcast</h1>
          <Tag color="blue">{broadcasts.length} broadcast</Tag>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters" ref={filtersRef}>
            <div ref={searchRef}>
              <Search
                placeholder="Tìm theo tiêu đề hoặc nội dung"
                allowClear
                style={{ width: isMobile ? '100%' : 300 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <Select
              placeholder="Lọc theo nhãn người dùng"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setUserTagFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {userTags.map(tag => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Lọc theo trạng thái"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setStatusFilter(value)}
            >
              <Option value="pending">Chờ gửi</Option>
              <Option value="sending">Đang gửi</Option>
              <Option value="completed">Đã gửi</Option>
              <Option value="failed">Thất bại</Option>
              <Option value="scheduled">Đã lên lịch</Option>
            </Select>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setModalVisible(true);
              }}
              className="admin-button"
              ref={addButtonRef}
            >
              Tạo broadcast mới
            </Button>
            <Button
              type="default"
              icon={<UserOutlined />}
              onClick={openSendSingleUserModal}
              className="admin-button"
            >
              Gửi cho 1 người
            </Button>
          </div>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container" ref={tableContainerRef}>
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredBroadcasts}
            loading={loading}
            rowKey="id"
            scroll={{ x: isMobile ? undefined : 1500 }}
            pagination={{
              responsive: true,
              defaultPageSize: isMobile ? 5 : 10,
              position: isMobile ? ['bottomCenter'] : ['bottomRight']
            }}
          />
        </div>
      </div>

      <Modal
        title="Tạo Broadcast mới"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setIsModalTourOpen(false); // Reset modal tour state
          form.resetFields();
        }}
        footer={null}
        width={800}
        className="admin-modal"
        destroyOnClose={true}
      >
        <div className="admin-form-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 ref={modalTitleRef} style={{ margin: 0 }}>
              Tạo chiến dịch broadcast mới
            </h3>
            <Button
              type="primary"
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                setIsModalTourOpen(true);
              }}
              size="small"
            >
              Hướng dẫn điền form
            </Button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ recipientMode: 'tags' }}
          >
            <div className="admin-form-section">
              <h3 className="admin-form-section-title">Thông tin broadcast</h3>
              <div ref={messageFieldRef}>
                <Form.Item
                  name="messageId"
                  label="Chọn tin nhắn"
                  rules={[{ required: true, message: 'Vui lòng chọn tin nhắn' }]}
                >
                  <Select
                    placeholder="Chọn tin nhắn muốn gửi"
                    showSearch
                    optionFilterProp="children"
                  >
                    {messages.map(msg => (
                      <Option key={msg.id} value={msg.id}>
                        {msg.template_data?.header?.content}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div ref={userTagsFieldRef}>
                <Form.Item
                  name="recipientMode"
                  label="Đối tượng nhận"
                  rules={[{ required: true, message: 'Vui lòng chọn đối tượng nhận' }]}
                >
                  <Radio.Group>
                    <Radio.Button value="all">Tất cả người dùng có Zalo</Radio.Button>
                    <Radio.Button value="tags">Theo nhãn</Radio.Button>
                    <Radio.Button value="users">Danh sách người dùng</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const recipientMode = getFieldValue('recipientMode');

                    if (recipientMode === 'users') {
                      return (
                        <Form.Item
                          name="selectedUserIds"
                          label="Chọn danh sách người dùng"
                          rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 người dùng' }]}
                          extra="Chỉ hiển thị người dùng đã có Zalo User ID"
                        >
                          <Select
                            mode="multiple"
                            placeholder="Tìm theo tên hoặc số điện thoại"
                            showSearch
                            optionFilterProp="children"
                            allowClear
                          >
                            {availableUsersWithZalo
                              .map((u) => (
                                <Option key={u.id} value={u.id}>
                                  {u.name || 'Khách hàng'}{u.phoneNumber ? ` - ${u.phoneNumber}` : ''}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      );
                    }

                    if (recipientMode === 'all') {
                      return (
                        <Form.Item>
                          <Tag color="green">Broadcast sẽ gửi tới toàn bộ người dùng đã có Zalo User ID</Tag>
                        </Form.Item>
                      );
                    }

                    return (
                      <Form.Item
                        name="userTags"
                        label="Chọn nhãn người dùng"
                        extra="Không chọn nhãn sẽ gửi cho tất cả người dùng có Zalo User ID"
                      >
                        <Select
                          mode="multiple"
                          placeholder="Chọn nhãn người dùng"
                          showSearch
                          optionFilterProp="children"
                          allowClear
                        >
                          {userTags.map(tag => (
                            <Option key={tag} value={tag}>
                              {tag}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                  disabled={submitLoading}
                  className="admin-button"
                  ref={submitButtonRef}
                >
                  Tạo Broadcast
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>

        {/* Modal Tour component */}
        <ConfigProvider locale={viVN}>
          <Tour
            open={isModalTourOpen}
            onClose={() => setIsModalTourOpen(false)}
            steps={modalTourSteps}
            indicatorsRender={(current, total) => (
              <span>{current + 1} / {total}</span>
            )}
            type="primary"
            arrow={true}
            placement="bottom"
            mask={{
              style: {
                boxShadow: 'inset 0 0 15px #333',
              },
            }}
          />
        </ConfigProvider>
      </Modal>

      <Modal
        title="Chọn đối tượng gửi broadcast"
        visible={sendBroadcastModalVisible}
        onCancel={() => {
          setSendBroadcastModalVisible(false);
          setSendingBroadcastRecord(null);
          sendBroadcastForm.resetFields();
        }}
        onOk={() => sendBroadcastForm.submit()}
        okText="Tiếp tục"
        cancelText="Hủy"
        className="admin-modal"
        destroyOnClose
      >
        <div className="admin-form-container">
          <Form
            form={sendBroadcastForm}
            layout="vertical"
            onFinish={handleConfirmSendBroadcast}
            initialValues={{ recipientMode: 'all' }}
          >
            <Form.Item
              name="recipientMode"
              label="Đối tượng nhận"
              rules={[{ required: true, message: 'Vui lòng chọn đối tượng nhận' }]}
            >
              <Radio.Group>
                <Radio.Button value="all">Tất cả người dùng có Zalo</Radio.Button>
                <Radio.Button value="tags">Theo nhãn</Radio.Button>
                <Radio.Button value="users">Danh sách người dùng</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const recipientMode = getFieldValue('recipientMode');

                if (recipientMode === 'users') {
                  return (
                    <Form.Item
                      name="selectedUserIds"
                      label="Chọn danh sách người dùng"
                      rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 người dùng' }]}
                      extra="Chỉ hiển thị người dùng đã có Zalo User ID"
                    >
                      <Select
                        mode="multiple"
                        placeholder="Tìm theo tên hoặc số điện thoại"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {availableUsersWithZalo
                          .map((u) => (
                            <Option key={u.id} value={u.id}>
                              {u.name || 'Khách hàng'}{u.phoneNumber ? ` - ${u.phoneNumber}` : ''}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  );
                }

                if (recipientMode === 'tags') {
                  return (
                    <Form.Item
                      name="userTags"
                      label="Chọn nhãn người dùng"
                      extra="Không chọn nhãn sẽ gửi cho tất cả người dùng có Zalo User ID"
                    >
                      <Select
                        mode="multiple"
                        placeholder="Chọn nhãn người dùng"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {userTags.map(tag => (
                          <Option key={tag} value={tag}>
                            {tag}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }

                return (
                  <Tag color="green">Broadcast sẽ gửi tới toàn bộ người dùng đã có Zalo User ID</Tag>
                );
              }}
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        title="Hẹn giờ gửi Broadcast"
        visible={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          setScheduledDateTime(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setScheduleModalVisible(false);
            setScheduledDateTime(null);
          }}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={scheduleLoading}
            onClick={handleScheduleSubmit}
            className="admin-button"
          >
            Xác nhận
          </Button>
        ]}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <div className="admin-form-section">
            <Form layout="vertical">
              <Form.Item
                label="Chọn thời gian gửi"
                required
                rules={[{ required: true, message: 'Vui lòng chọn thời gian gửi' }]}
              >
                <DatePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Chọn thời gian"
                  onChange={(value) => setScheduledDateTime(value ? moment(value.valueOf()) : null)}
                  disabledDate={(current) => current && current < moment().startOf('day')}
                  disabledTime={(current) => {
                    if (!current) return {};

                    const now = moment();
                    const selectedDate = moment(current.valueOf());

                    if (selectedDate.isSame(now, 'day')) {
                      return {
                        disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                        disabledMinutes: (selectedHour) =>
                          selectedHour === now.hour()
                            ? Array.from({ length: now.minute() }, (_, i) => i)
                            : [],
                        disabledSeconds: (selectedHour, selectedMinute) =>
                          selectedHour === now.hour() && selectedMinute === now.minute()
                            ? Array.from({ length: now.second() }, (_, i) => i)
                            : []
                      };
                    }
                    return {};
                  }}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form>
          </div>
        </div>
      </Modal>

      <Modal
        title="Gửi tin nhắn cho 1 người dùng"
        visible={sendSingleUserModalVisible}
        onCancel={() => {
          setSendSingleUserModalVisible(false);
          sendSingleUserForm.resetFields();
        }}
        footer={null}
        width={600}
        className="admin-modal"
        destroyOnClose
      >
        <div className="admin-form-container">
          <Form
            form={sendSingleUserForm}
            layout="vertical"
            onFinish={handleSendToSingleUser}
          >
            <div className="admin-form-section">
              <Form.Item
                name="messageId"
                label="Chọn tin nhắn"
                rules={[{ required: true, message: 'Vui lòng chọn tin nhắn' }]}
              >
                <Select
                  placeholder="Chọn tin nhắn muốn gửi"
                  showSearch
                  optionFilterProp="children"
                >
                  {messages.map(msg => (
                    <Option key={msg.id} value={msg.id}>
                      {msg.template_data?.header?.content || 'Tin nhắn không có tiêu đề'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="userId"
                label="Chọn người dùng"
                rules={[{ required: true, message: 'Vui lòng chọn người dùng' }]}
              >
                <Select
                  placeholder="Nhập tên hoặc số điện thoại để tìm kiếm"
                  showSearch
                  loading={loadingUsers}
                  optionFilterProp="children"
                  notFoundContent={loadingUsers ? <Spin size="small" /> : 'Không tìm thấy người dùng'}
                  allowClear
                >
                  {usersList.map(u => (
                    <Option key={u.id} value={u.id}>
                      {u.name || 'Khách hàng'}{u.phoneNumber ? ' - ' + u.phoneNumber : ''}{u.zaloUserId ? ' (Đã có Zalo)' : ' (Chưa có Zalo)'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={sendSingleUserLoading}
                  disabled={sendSingleUserLoading}
                  className="admin-button"
                  block
                >
                  Gửi tin nhắn
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>
    </AdminLayout>
  );
};

export default BroadcastManagement; 
